import type { PayloadAction } from 'modules/global/generic.types';
import type { MayBe } from 'modules/global/typing/common/common';
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

import type { GlobalConfig, WrapperReducerConfig } from './reducers.types';
import { isStartRequest, isSuccessRequest, isFailureRequest, readEntityId } from './utils';

/**
 * The ids of one list. `T` is the entity the SUCCESS payload carries: its `id` is read by default,
 * or whatever `config.selectId` returns.
 * @template T
 */
export type IdsSlice = Array<string>;

/** What a paginated SUCCESS payload must carry for this reducer — nothing else is read. */
type ContentPayload<T> = { content: Array<T> };

/** What a REQUEST payload must carry: the page marker telling a first page from a next one. */
type PagedRequestPayload = {
  request: {
    params?: { page?: unknown };
    data?: { page?: unknown };
  };
};

const initialState: IdsSlice = [];

const defaultSelectId = readEntityId;

/**
 * Every consumer's entities resolve to an id — their own field through `selectId`, or `id`. An
 * entity that resolves to none is appended as `undefined`, which is the behaviour this reducer has
 * always had (locked by `__tests__/generic.ids.spec.ts`); the slice is still typed as a list of ids
 * because no consumer produces that case.
 */
const asIds = (ids: Array<MayBe<string>>): IdsSlice => ids as IdsSlice;

const selectEntities = <T>(entities: Array<T>, config?: GlobalConfig<T>): Array<T> => {
  const filter = config?.filter;
  return typeof filter === 'function' ? entities.filter(filter) : entities;
};

/** Replaces the list with the ids of the page — or appends to it when the slice accumulates pages. */
export const setAll =
  <T>(config?: GlobalConfig<T>) =>
    (state: IdsSlice, { payload }: PayloadAction<ContentPayload<T>>): IdsSlice => {
      const selectId = config?.selectId ?? defaultSelectId;
      const elements = config?.paginationSlice ? [] : state;

      return asIds(uniq([...elements, ...selectEntities(payload.content, config).map(entity => selectId(entity))]));
    };

/** Appends the ids of the page to the list, whatever the pagination mode. */
export const addMany =
  <T>(config?: GlobalConfig<T>) =>
    (state: IdsSlice, { payload }: PayloadAction<ContentPayload<T>>): IdsSlice => {
      const selectId = config?.selectId ?? defaultSelectId;

      return asIds(uniq([...state, ...selectEntities(payload.content, config).map(entity => selectId(entity))]));
    };

export const clearAll = (): IdsSlice => initialState;

/**
 * A request for the first page starts a fresh list; a request for a next page keeps it. `paginationSlice`
 * and `meta.skipReset` both opt out of the reset.
 */
export const beforeRequest =
  <T>(config?: GlobalConfig<T>) =>
    (state: IdsSlice, { payload, meta = {} }: PayloadAction<PagedRequestPayload>): IdsSlice => {
      const { request } = payload;
      const shouldReset = !config?.paginationSlice && !meta.skipReset && !request.params?.page && !request.data?.page;
      return shouldReset ? initialState : state;
    };

const genericIdsReducer = <T = { id: string }>(
  actionRoot: string,
  { reducers = {}, matchers = [], global: config }: WrapperReducerConfig<IdsSlice, T> = {}
) =>
    createReducer<IdsSlice>(initialState, reducers, [
      {
        matcher: isStartRequest(actionRoot, config),
        reducer: beforeRequest(config),
      },
      {
        matcher: isSuccessRequest(actionRoot, config),
        reducer: setAll(config),
      },
      {
        matcher: isFailureRequest(actionRoot, config),
        reducer: clearAll,
      },
      ...matchers,
    ]);

export default genericIdsReducer;
